// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Source, Layer } from 'react-map-gl';
import { useTheme } from '@mui/system';
import { useAppDispatch, useAppSelector, useAppStore } from 'Hooks';
import { clearAllSelected, toggleSelected } from 'Features/fileSelection/fileSelectionSlice';
import { ResetFootprintKindsAction } from '../mapCommonActions';
import { getVisibleAllFootprints, getVisibleTifFootprints, getFootprintNumbers } from '../footprintSelectors';
import {
  buildFootprintCollection, buildFootprintOutlineCollection, footprintFillPaint,
  LAS_COLOR, PANO_COLOR, KIND_DASHES, KIND_ORDER, kindLineColor,
} from './footprintGeoJson';
import { extentsSurfaceEnabled, extentsCardEnabled } from './extentsConfig';
import FootprintsSwitch from './FootprintsSwitch';
import FootprintCornerLabels from './FootprintCornerLabel';
import FootprintPopupCard from './FootprintPopupCard';
import useFootprintInteractions, { footprintGesturesBlocked } from './useFootprintInteractions';
import useFootprintFlash from './useFootprintFlash';

interface CardState {
  files: IFileRef[];
  lngLat: [number, number];
}

interface ComponentProps {
  showLayers: boolean;
  includeLas: boolean;
}

// One footprint polygon per input tif/LAS on the shared Mapbox map. The fill
// layer is ALWAYS present at (near-)zero opacity so hover/click hit-testing
// works with the switch off; outlines only appear on hover, selection, or when
// the "Show File Extents" switch is on.
export const MapFileFootprints: React.FC<ComponentProps> = ({ showLayers, includeLas }) => {
  const theme = useTheme();
  // Cyan, NOT the brand blue: the KML processing-area fill owns that blue
  // (rgba(1,161,221,0.2)) and footprint outlines must never blend into it
  // (D33, reaffirmed D73 after a live try of the blue).
  const tifColor = theme.palette.secondary.main;

  const allFootprints = useAppSelector(getVisibleAllFootprints);
  const tifFootprints = useAppSelector(getVisibleTifFootprints);
  const footprints = includeLas ? allFootprints : tifFootprints;

  const hovered = useAppSelector((state) => state.fileSelection.hovered);
  const selected = useAppSelector((state) => state.fileSelection.selected);
  const footprintNumbers = useAppSelector(getFootprintNumbers);
  const footprintsOn = useAppSelector((state) => state.map.common.layers.fileFootprints);
  // 2D viewer: footprints follow the Orthomosaic master checkbox, like rasters.
  const masterVisible = useAppSelector((state) => !showLayers || state.map.common.layers.orthomosaic);

  // The switch card's per-kind filter (D78). A filtered kind is absent from
  // the MAP entirely - both geojson collections lose its features, so draw,
  // hover hit-area, captions and even selection fills die together. Sidebar
  // rows and the fileSelection state are untouched.
  const hiddenKinds = useAppSelector((state) => state.map.common.hiddenFootprintKinds);
  const shownFootprints = useMemo(
    () => (hiddenKinds.length ? footprints.filter((fp) => !hiddenKinds.includes(fp.kind)) : footprints),
    [footprints, hiddenKinds],
  );
  // Kinds with data on THIS pane (pre-filter), in stack order: the switch card
  // only offers the list when there are at least two.
  const presentKinds = useMemo(
    () => [...new Set(footprints.map((fp) => fp.kind))]
      .sort((a, b) => KIND_ORDER[a] - KIND_ORDER[b]),
    [footprints],
  );

  const collection = useMemo(
    () => buildFootprintCollection(shownFootprints, hovered, selected),
    [shownFootprints, hovered, selected],
  );
  const outlineCollection = useMemo(
    () => buildFootprintOutlineCollection(shownFootprints, hovered, selected),
    [shownFootprints, hovered, selected],
  );

  // A click opens the card on its hit stack; an empty click closes it.
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const [card, setCard] = useState<CardState | null>(null);
  const onFootprintClick = useCallback((files: IFileRef[], lngLat: [number, number]) => {
    if (!files.length) {
      setCard(null);
      return;
    }
    // A SINGLE file toggles on the click itself: selecting shows the receipt
    // card (name + download right there); DEselecting stays silent (D58). A
    // stack selects nothing and opens the pager card as the control (D55).
    if (files.length === 1) {
      const { kind, id } = files[0];
      const wasSelected = store.getState().fileSelection.selected[kind].includes(id);
      dispatch(toggleSelected({ kind, id }));
      setCard(wasSelected ? null : { files, lngLat });
      return;
    }
    setCard({ files, lngLat });
  }, []);
  useFootprintInteractions(onFootprintClick);

  // Starting a draw or measurement closes the card AND wipes the selection
  // (D49/D51): the task changed, and stale highlights clutter the tool.
  const drawBlocked = useAppSelector((state) => footprintGesturesBlocked(state.map.draw.present));
  useEffect(() => {
    if (drawBlocked) {
      setCard(null);
      dispatch(clearAllSelected());
    }
  }, [drawBlocked]);

  // The kind filter is a viewing tool, not a setting: leaving the map page
  // resets it, so no pane ever inherits an invisible filter it cannot show
  // a list for (D77b/D78).
  useEffect(() => () => {
    dispatch(ResetFootprintKindsAction());
  }, []);

  // Memoized: a fresh array every render would reset the card's pager (its
  // page-flip dispatches re-render this component). Re-resolving on footprint
  // changes still drops a file deleted mid-view.
  const cardFiles = useMemo(
    () => (card?.files || [])
      .map((ref) => shownFootprints.find((fp) => fp.kind === ref.kind && fp.id === ref.id))
      .filter((fp): fp is (typeof shownFootprints)[number] => !!fp),
    [card, shownFootprints],
  );

  const footprintFlash = useFootprintFlash();

  // Surface gate (extentsConfig): the customer Project Details map carries
  // no extents at all - rows keep selecting for the bulk pills, the map
  // never reacts. Admin keeps the full system; the 2D viewer is imagery-only
  // by its includeLas prop.
  const surfaceEnabled = extentsSurfaceEnabled(showLayers);

  if (!footprints.length || !masterVisible || !surfaceEnabled) return null;

  const lineWidth: mapboxgl.Expression = [
    'case', ['get', 'hovered'], 4, ['get', 'selected'], 3, 2,
  ];
  // One step heavier than tif: the dash gaps make equal widths read thinner.
  const lasLineWidth: mapboxgl.Expression = [
    'case', ['get', 'hovered'], 5, ['get', 'selected'], 4, 3,
  ];
  const lineOpacity: mapboxgl.Expression = [
    'case', ['get', 'hovered'], 1, ['get', 'selected'], 1, footprintsOn ? 0.9 : 0,
  ];

  return (
    <>
      <Source id="file-footprints" type="geojson" data={collection}>
        <Layer
          id="file-footprints-fill"
          source="file-footprints"
          type="fill"
          beforeId="kmlStub"
          paint={footprintFillPaint(tifColor, LAS_COLOR, PANO_COLOR)}
        />
      </Source>
      {/* Outlines draw LINESTRINGS from an UNBUFFERED source: the default tile
          buffer makes both tiles adjacent to a boundary draw a nearby edge with
          different dash phases (dense dot-dash doubling), and clipping POLYGONS
          paints phantom ring-closing edges along boundaries (D70). */}
      <Source id="file-footprints-lines" type="geojson" data={outlineCollection} buffer={0}>
        {!hiddenKinds.includes('tif') && (
          <Layer
            id="file-footprints-line-tif"
            source="file-footprints-lines"
            type="line"
            beforeId="kmlStub"
            filter={['==', ['get', 'kind'], 'tif']}
            paint={{
              'line-color': tifColor,
              'line-width': lineWidth,
              'line-opacity': lineOpacity,
            }}
          />
        )}
        {!hiddenKinds.includes('las') && (
          <Layer
            id="file-footprints-line-las"
            source="file-footprints-lines"
            type="line"
            beforeId="kmlStub"
            filter={['==', ['get', 'kind'], 'las']}
            paint={{
              'line-color': LAS_COLOR,
              'line-width': lasLineWidth,
              'line-opacity': lineOpacity,
              'line-dasharray': KIND_DASHES.las,
            }}
          />
        )}
        {!hiddenKinds.includes('pano') && (
          <Layer
            id="file-footprints-line-pano"
            source="file-footprints-lines"
            type="line"
            beforeId="kmlStub"
            filter={['==', ['get', 'kind'], 'pano']}
            paint={{
              'line-color': PANO_COLOR,
              'line-width': lasLineWidth,
              'line-opacity': lineOpacity,
              'line-dasharray': KIND_DASHES.pano,
            }}
          />
        )}
        {footprintFlash && !hiddenKinds.includes(footprintFlash.kind) && (
          <Layer
            id="file-footprints-flash"
            source="file-footprints-lines"
            type="line"
            beforeId="kmlStub"
            filter={['all',
              ['==', ['get', 'kind'], footprintFlash.kind],
              ['==', ['get', 'id'], footprintFlash.id],
            ] as mapboxgl.Expression}
            paint={{
              'line-color': kindLineColor(footprintFlash.kind, tifColor),
              'line-width': footprintFlash.width,
              'line-opacity': footprintFlash.opacity,
            }}
          />
        )}
      </Source>
      <FootprintCornerLabels footprints={shownFootprints} footprintsOn={footprintsOn} numbers={footprintNumbers} />
      {card && cardFiles.length > 0 && (
        <FootprintPopupCard files={cardFiles} numbers={footprintNumbers} lngLat={card.lngLat} onClose={() => setCard(null)} />
      )}
      {/* The extents card (master switch + per-kind list). Customers toggle
          imagery from the Orthomosaic pane instead. */}
      {extentsCardEnabled() && <FootprintsSwitch kinds={presentKinds} />}
    </>
  );
};

export default MapFileFootprints;
